import { TFunction } from '@psyomics/ui-common';
import { Button, ColumnDef, CellDateFormatter, useFormatCellAsDate } from '@psyomics/ui-components';
import { Eye, Loader, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReferralSessionStatus } from '../generated/home';

import { isProcessingReferralRow, SubmittedReferralRow } from './types';

function patientNameColumn(t: TFunction): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) =>
      isProcessingReferralRow(row)
        ? `${row.patientGivenName} ${row.patientFamilyName}`
        : `${row.referral.patient.givenName} ${row.referral.patient.familyName}`,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.patientName'),
    id: 'patientName',
  };
}

function piSystemIdColumn(t: TFunction): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) => (isProcessingReferralRow(row) ? row.piSystemId : row.referral.patient.piSystemId.id),
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.piSystemId'),
    id: 'piSystemId',
  };
}

function organisationColumn(t: TFunction): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) => (isProcessingReferralRow(row) ? row.organisationName : row.referral.organisation.name),
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.organisation'),
    id: 'organisation',
  };
}

function serviceColumn(t: TFunction): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) => (isProcessingReferralRow(row) ? row.serviceDescription : row.referral.service.description),
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.service'),
    id: 'service',
  };
}

function submittedAtColumn(
  dateFormatter: CellDateFormatter<SubmittedReferralRow>,
  t: TFunction
): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) => (isProcessingReferralRow(row) ? row.submittedAt : row.createdAt),
    cell: dateFormatter,
    enableSorting: false,
    header: t('page.professionalReferral.home.table.submittedAt'),
    id: 'createdAt',
  };
}

function expiresAtColumn(
  dateFormatter: CellDateFormatter<SubmittedReferralRow>,
  t: TFunction
): ColumnDef<SubmittedReferralRow> {
  return {
    accessorFn: (row) => (isProcessingReferralRow(row) ? null : row.expiresAt),
    cell: dateFormatter,
    enableSorting: false,
    header: t('page.professionalReferral.home.table.expiresAt'),
    id: 'expiresAt',
  };
}

function statusColumn(t: TFunction): ColumnDef<SubmittedReferralRow> {
  return {
    cell: (info) => {
      const row = info.row.original;

      if (!isProcessingReferralRow(row)) return t(`model.referralSession.status.${row.status}`);

      if (row.delayed) return <output>{t('page.professionalReferral.home.table.delayed')}</output>;

      return (
        <output className="inline-flex items-center gap-2">
          <Loader aria-hidden className="h-4 w-4 animate-spin text-[var(--color-primary-500)]" />
          {t('page.professionalReferral.home.table.sending')}
        </output>
      );
    },
    enableSorting: false,
    header: t('page.professionalReferral.home.table.status'),
    id: 'status',
  };
}

function actionsColumn(t: TFunction, onCancel: (id: string) => void): ColumnDef<SubmittedReferralRow> {
  return {
    id: 'actions',
    header: '',
    cell: (info) => {
      const row = info.row.original;

      if (isProcessingReferralRow(row)) return null;

      const { referralRequestId, status } = row;
      const viewLabel = t('common.buttons.view');

      return (
        <div className="flex gap-2">
          <Link to={`/referral-request/${referralRequestId}/view`}>
            <Button appearance="secondary" aria-label={viewLabel} icon={Eye} size="sm" title={viewLabel} />
          </Link>
          {status === ReferralSessionStatus.Pending && (
            <Button
              appearance="secondary"
              aria-label={t('page.professionalReferral.home.table.cancelReferral')}
              icon={X}
              onClick={() => onCancel(referralRequestId)}
              size="sm"
              title={t('common.buttons.cancel')}
            />
          )}
        </div>
      );
    },
  };
}

const useTableColumns = (onCancel: (id: string) => void): ColumnDef<SubmittedReferralRow>[] => {
  const { t } = useTranslation();
  const dateFormatter = useFormatCellAsDate<SubmittedReferralRow>(t, 'datetime.short');

  return [
    patientNameColumn(t),
    piSystemIdColumn(t),
    organisationColumn(t),
    serviceColumn(t),
    submittedAtColumn(dateFormatter, t),
    expiresAtColumn(dateFormatter, t),
    statusColumn(t),
    actionsColumn(t, onCancel),
  ];
};

export { useTableColumns };
